import { useState, useEffect } from 'react'
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/20/solid'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useMutation, useLazyQuery } from '@apollo/client'
import { ADD_BOAT_ROUTE, UPDATE_BOAT_ROUTE } from 'graphql/route/mutations'
import { GET_BOAT_ROUTE_LIST } from 'graphql/route/queries'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { DAYS_OF_WEEK } from 'pages/constants'
import DaysOfWeekSelector from 'components/custom/DaysOfWeeKCheckBox'
import { BoatNameDropdown } from 'components/custom/BoatNameDropdown'
import { WeightConfigDisplay } from 'components/custom/WeightConfigDisplay'
import { RouteListMasterDropdown } from './RouteListMasterDropdown'
import { SeatLayoutDropdown } from './SeatLayoutDropdown'
import { TripTypeDropdown } from './TripTypeDropdown'
import TripAlert from '../TripAlert'

export default function MetroRouteForm() {
    const { id } = useParams()
    console.log('id===', id)
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [addBoatError, setAddBoatError] = useState(t('Problem Adding Route'))
    const [submitLoading, setSubmitLoading] = useState(false)
    const [roteInfo, setRouteInfo] = useState(null)
    const [initialWeekDays, setInitialWeekDays] = useState([])
    const [masterRouteSelected, setMasterRouteSelected] = useState(null)
    const [selectedBoat, setSelectedBoat] = useState(null)
    const [selectedSeatLayout, setSelectedSeatLayout] = useState(null)
    const [selectedTripType, setSelectedTripType] = useState(null)

    useEffect(() => {
        if (id) {
            getRouteList({
                variables: {
                    page: {
                        limit: 1,
                        offset: 0,
                    },
                    routeId: id,
                    routeName: null,
                    statusCode: null,
                },
            })
        } else {
            console.log('No id found')
            setRouteInfo(null)
            RouteFormValidation.resetForm()
        }
    }, [id])

    // For Edit Route
    const [getRouteList, { loading: routeListLoading, error: routeListError }] = useLazyQuery(GET_BOAT_ROUTE_LIST, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            const routeData = data.getBoatRoute.boat_routes[0]
            setRouteInfo({
                ...routeData,
            })
            console.log('routeData.waypoints===', routeData.days_of_week)
            setInitialWeekDays(routeData.days_of_week)
            console.log('routeData.route_name===', roteInfo?.master_route?.route_name.en)
            setMasterRouteSelected(routeData.master_route)

            // Set the selected boat if boat_id exists
            if (routeData.boat_id) {
                // We need to fetch the boat details to get weight_config
                // For now, we'll set a placeholder that will be updated when the boat dropdown loads
                setSelectedBoat({ id: routeData.boat_id, name: { en: 'Loading...' } })
            }

            // Set seat layout and trip type if they exist
            if (routeData.active_seat_layout) {
                setSelectedSeatLayout(routeData.active_seat_layout)
            }
            if (routeData.trip_type) {
                setSelectedTripType(routeData.trip_type)
            }
        },
    })
    //  MUtation Add Route
    const [addBoatRoute, { loading: addLoading }] = useMutation(ADD_BOAT_ROUTE, {
        onCompleted: data => {
            if (data?.addBoatRoute.code === '200' && data?.addBoatRoute.success === true) {
                RouteFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setShowSucDialog(true)
                // Time delay to redirect to the users page
                setTimeout(() => {
                    handleBack()
                }, 500)
            } else {
                RouteFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setAddBoatError(data?.addBoatRoute.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            RouteFormValidation.setSubmitting(false)
            setAddBoatError(error.message)
            setShowErrDialog(true)
        },
    })
    // Mutation Update Route
    const [updateBoatRoute, { loading: updateLoading }] = useMutation(UPDATE_BOAT_ROUTE, {
        onCompleted: data => {
            if (data?.updateBoatRoute.code === '200' && data?.updateBoatRoute.success === true) {
                RouteFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setShowSucDialog(true)
                // Time delay to redirect to the users page
                setTimeout(() => {
                    handleBack()
                }, 500)
            } else {
                RouteFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setAddBoatError(data?.UpdateBoatRoute.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            RouteFormValidation.setSubmitting(false)
            setAddBoatError(error.message)
            setShowErrDialog(true)
        },
    })

    const submitBoatRoute = async routeInfo => {
        try {
            console.log('routeInfo===', routeInfo)
            RouteFormValidation.setSubmitting(true)
            // const wayPoints = prepareWaypoints(routeInfo)
            const formattedData = {
                boatRoute: {
                    route_name: {
                        en: RouteFormValidation.values.trip_name_en,
                        ar: RouteFormValidation.values.trip_name_ar || RouteFormValidation.values.trip_name_en,
                    },
                    master_route_id: masterRouteSelected?.master_route_id || null,
                    status_code: routeInfo.routeStatusCode,
                    boat_id: routeInfo.boatId,
                    days_of_week: routeInfo.daysOfWeek,
                    seat_layout_id: routeInfo.seatLayoutId || null,
                    trip_type_code: routeInfo.tripTypeCode || null,
                    trip_tag: routeInfo.tripTag || null,
                    is_charter: routeInfo.is_charter || false,
                },
            }
            if (id) {
                formattedData['routeId'] = id
                updateBoatRoute({
                    variables: formattedData,
                })
            } else {
                addBoatRoute({
                    variables: formattedData,
                })
            }
            console.log('formattedData======>', formattedData)

            console.log('formattedData===', 'Done')
        } catch (error) {
            RouteFormValidation.setSubmitting(false)
        }
    }
    console.log('roteInfo-----------===', roteInfo)
    // Form Validation
    const RouteFormValidation = useFormik({
        enableReinitialize: true,
        initialValues: {
            trip_name_en: roteInfo?.route_name?.en || '',
            trip_name_ar: roteInfo?.route_name?.ar || '',
            route_name: roteInfo?.master_route?.master_route_id || '',
            routeStatusCode: roteInfo?.status_code || '',
            boatId: roteInfo?.boat_id || '',
            daysOfWeek: initialWeekDays || [],
            seatLayoutId: roteInfo?.seat_layout_id || '',
            tripTypeCode: roteInfo?.trip_type_code || '',
            tripTag: roteInfo?.trip_tag || '',
            is_charter: roteInfo?.is_charter === true || roteInfo?.is_charter === 1 || roteInfo?.is_charter === '1',
        },
        validationSchema: Yup.object().shape({
            trip_name_en: Yup.string().required(t('Trip Name is required')),
            route_name: Yup.string().required(t('Route Name is required')),
            boatId: Yup.string().required(t('Boat is required')),
            routeStatusCode: Yup.string().required(t('Status is required')),
            daysOfWeek: Yup.array().min(1, 'At least one day must be selected').required('Days of week is required'),
            seatLayoutId: Yup.string().required(t('Seat Layout is required')),
            tripTypeCode: Yup.string().required(t('Trip Type is required')),
        }),
        onSubmit: values => {
            console.log('Form Data====:', values)
            setSubmitLoading(true)
            submitBoatRoute(values)
        },
    })

    const handleBack = () => {
        navigate('/metro-routes')
    }
    const handleDialogClose = () => {
        setShowSucDialog(false)
        setShowErrDialog(false)
    }

    const handleBoatSelect = boat => {
        setSelectedBoat(boat)
    }

    const handleSeatLayoutSelect = layout => {
        setSelectedSeatLayout(layout)
    }

    const handleTripTypeSelect = type => {
        setSelectedTripType(type)
    }

    return (
        <>
            <div>
                <div className="mb-8 flex justify-between items-center">
                    <button
                        onClick={handleBack}
                        type="button"
                        className="inline-flex items-center gap-x-0.5 rounded-md text-sm text-gray-600"
                    >
                        <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-4 rtl:rotate-180" />
                        {t('Back')}
                    </button>
                    <div className="flex gap-3">
                        <button
                            onClick={handleBack}
                            type="button"
                            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        >
                            {t('Cancel')}
                        </button>
                        <button
                            disabled={RouteFormValidation.isSubmitting}
                            type="button"
                            onClick={() => {
                                console.log('RouteFormValidation VALUES====:', RouteFormValidation)
                                RouteFormValidation.handleSubmit()
                            }}
                            href="/metro-routes"
                            className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                        >
                            {t('Save')}
                        </button>
                    </div>
                </div>
                <TripAlert />
                <form className="mb-8">
                    <div className="space-y-12">
                        <div className="grid grid-col</form>s-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                            <div>
                                <h2 className="text-base font-semibold leading-7 text-gray-900">
                                    {t('Route Details')} &nbsp;
                                </h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">
                                    {t('Provide the details of the route')}
                                </p>
                            </div>

                            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                                <div className="sm:col-span-full">
                                    <label
                                        htmlFor="trip-name"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Trip Name English')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <input
                                        id="trip_name_en"
                                        name="trip_name_en"
                                        type="text"
                                        value={RouteFormValidation.values.trip_name_en}
                                        placeholder="Enter here"
                                        onChange={RouteFormValidation.handleChange}
                                        onBlur={RouteFormValidation.handleBlur}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {RouteFormValidation.touched.trip_name_en &&
                                        RouteFormValidation.errors.trip_name_en && (
                                            <p className="text-error text-xs mt-1">
                                                {RouteFormValidation.errors.trip_name_en}
                                            </p>
                                        )}
                                </div>
                                <div className="sm:col-span-full">
                                    <label
                                        htmlFor="trip-name"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Trip Name Arabic')}
                                    </label>
                                    <input
                                        id="trip_name_ar"
                                        name="trip_name_ar"
                                        type="text"
                                        value={RouteFormValidation.values.trip_name_ar}
                                        placeholder="Enter here"
                                        onChange={RouteFormValidation.handleChange}
                                        onBlur={RouteFormValidation.handleBlur}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                </div>
                                <div className="sm:col-span-full">
                                    <label
                                        htmlFor="route_name"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Route Name')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <RouteListMasterDropdown
                                        fields={{
                                            ...RouteFormValidation.getFieldProps('route_name'),
                                            disabled: id ? !roteInfo?.editable?.waypoints : false,
                                        }}
                                        purpose="add"
                                        className="w-full"
                                        onChangeData={obj => {
                                            console.log('Onchange value--', obj)
                                            if (obj.master_route_id) {
                                                RouteFormValidation.setFieldValue('routeStatusCode', obj.status.code)
                                                setMasterRouteSelected(obj)
                                            }
                                        }}
                                    />
                                    {RouteFormValidation.touched.route_name &&
                                        RouteFormValidation.errors.route_name && (
                                            <p className="text-error text-xs mt-1">
                                                {RouteFormValidation.errors.route_name}
                                            </p>
                                        )}
                                </div>
                            </div>
                            <div>
                                <h2 className="text-base font-semibold leading-7 text-gray-900">{t('')} &nbsp;</h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">{t('')}</p>
                            </div>

                            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                                <div className="sm:col-span-full">
                                    <label
                                        htmlFor="trip-name"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Way Points')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {masterRouteSelected?.waypoints?.map((waypoint, index) => {
                                            return (
                                                <>
                                                    <span
                                                        key={waypoint.location_id}
                                                        className="inline-flex items-center px-4 py-3 text-sm rounded-full bg-gray-50 text-black"
                                                    >
                                                        {waypoint.location?.name?.en}
                                                    </span>
                                                    {index !== masterRouteSelected?.waypoints.length - 1 && (
                                                        <span
                                                            key={waypoint.location.name.en}
                                                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full bg-white text-primary"
                                                        >
                                                            <ArrowRightIcon
                                                                aria-hidden="true"
                                                                className="-ml-0.5 h-5 w-5"
                                                            />
                                                        </span>
                                                    )}
                                                </>
                                            )
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="space-y-12 mt-6">
                        <div className="grid grid-cols-1 gap-x-8 gap-y-4 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                            <div>
                                <h2 className="text-base font-semibold leading-7 text-gray-900">
                                    {t('Boat Details')} &nbsp;
                                </h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">
                                    {t('Select the Boat and the days of operation')}
                                </p>
                            </div>
                            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                                <div className="sm:col-span-full">
                                    <div className="flex items-center">
                                        <input
                                            id="is_charter"
                                            name="is_charter"
                                            type="checkbox"
                                            checked={Boolean(RouteFormValidation.values.is_charter)}
                                            onChange={e =>
                                                RouteFormValidation.setFieldValue('is_charter', e.target.checked)
                                            }
                                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                        />
                                        <label
                                            htmlFor="is_charter"
                                            className="ml-3 block text-sm font-medium text-gray-900"
                                        >
                                            {t('Charter Boat')}
                                        </label>
                                    </div>
                                </div>

                                <div className="sm:col-span-full">
                                    <label
                                        htmlFor="captain"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Boat')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <div className="mt-2">
                                        <BoatNameDropdown
                                            fields={RouteFormValidation.getFieldProps('boatId')}
                                            purpose="add"
                                            status="ACT"
                                            onBoatSelect={handleBoatSelect}
                                        />
                                        {RouteFormValidation.touched.boatId && RouteFormValidation.errors.boatId && (
                                            <p className="text-error text-xs mt-1">
                                                {RouteFormValidation.errors.boatId}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Weight Configuration Display */}
                                {selectedBoat && (
                                    <div className="sm:col-span-full">
                                        <WeightConfigDisplay
                                            weightConfig={selectedBoat.weight_config}
                                            boatName={selectedBoat.name?.en}
                                        />
                                    </div>
                                )}

                                <div className="sm:col-span-full">
                                    <label
                                        htmlFor="seatLayoutId"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Seat Layout')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <p className="text-xs text-gray-500 mb-2">
                                        {t('Select the seat layout that will appear for customers when booking')}
                                    </p>
                                    <div className="mt-2">
                                        <SeatLayoutDropdown
                                            fields={RouteFormValidation.getFieldProps('seatLayoutId')}
                                            onChangeData={handleSeatLayoutSelect}
                                        />
                                        {RouteFormValidation.touched.seatLayoutId &&
                                            RouteFormValidation.errors.seatLayoutId && (
                                                <p className="text-error text-xs mt-1">
                                                    {RouteFormValidation.errors.seatLayoutId}
                                                </p>
                                            )}
                                    </div>
                                </div>

                                <div className="sm:col-span-full">
                                    <label
                                        htmlFor="tripTypeCode"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Trip Type')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <p className="text-xs text-gray-500 mb-2">
                                        {t(
                                            'Choose the Trip Type to make the trip available for one-way, round-trip, or both'
                                        )}
                                    </p>
                                    <div className="mt-2">
                                        <TripTypeDropdown
                                            fields={RouteFormValidation.getFieldProps('tripTypeCode')}
                                            onChangeData={handleTripTypeSelect}
                                        />
                                        {RouteFormValidation.touched.tripTypeCode &&
                                            RouteFormValidation.errors.tripTypeCode && (
                                                <p className="text-error text-xs mt-1">
                                                    {RouteFormValidation.errors.tripTypeCode}
                                                </p>
                                            )}
                                    </div>
                                </div>

                                <div className="sm:col-span-full">
                                    <label
                                        htmlFor="tripTag"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Trip Tag')}
                                    </label>
                                    <p className="text-xs text-gray-500 mb-2">
                                        {t('Optional tag for linking promotional popups to this trip')}
                                    </p>
                                    <div className="mt-2">
                                        <input
                                            id="tripTag"
                                            name="tripTag"
                                            type="text"
                                            value={RouteFormValidation.values.tripTag}
                                            placeholder={t('Enter trip tag (optional)')}
                                            onChange={RouteFormValidation.handleChange}
                                            onBlur={RouteFormValidation.handleBlur}
                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="space-y-12 mt-6">
                        <div className="grid grid-cols-1 gap-x-8 gap-y-4 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                            <div>
                                <h2 className="text-base font-semibold leading-7 text-gray-900">
                                    {t('Days of Operation')} &nbsp;
                                </h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">
                                    {t('Provide the details of route that operates and their boats')}
                                </p>
                            </div>

                            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                                <DaysOfWeekSelector
                                    days={DAYS_OF_WEEK}
                                    selectedDays={RouteFormValidation.values.daysOfWeek}
                                    onChange={e => {
                                        const value = e.target.value
                                        const selectedDays = RouteFormValidation.values.daysOfWeek
                                        if (selectedDays.includes(value)) {
                                            const filteredDays = selectedDays.filter(day => day !== value)
                                            RouteFormValidation.setFieldValue('daysOfWeek', filteredDays)
                                        } else {
                                            RouteFormValidation.setFieldValue('daysOfWeek', [...selectedDays, value])
                                        }
                                    }}
                                    error={
                                        RouteFormValidation.touched.daysOfWeek && RouteFormValidation.errors.daysOfWeek
                                    }
                                    touched={RouteFormValidation.touched.daysOfWeek}
                                />
                            </div>
                        </div>
                    </div>
                </form>
                <div className="mt-10 mb-8 flex justify-between items-center">
                    {/* <a href="/metro-routes" className="inline-flex items-center gap-x-1.5 rounded-md text-sm">
                        <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5" />
                        {t('Back')}
                    </a> */}
                    <p></p>
                    <div className="flex gap-3">
                        <button
                            onClick={handleBack}
                            type="button"
                            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        >
                            {t('Cancel')}
                        </button>
                        <button
                            disabled={RouteFormValidation.isSubmitting}
                            type="button"
                            onClick={() => {
                                RouteFormValidation.handleSubmit()
                            }}
                            href="/metro-routes"
                            className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                        >
                            {t('Save')}
                        </button>
                    </div>
                </div>
            </div>
            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={addBoatError} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog
                    status={true}
                    title={t('Success')}
                    message={t('Route Added Successfully')}
                    onClose={handleDialogClose}
                />
            )}
            {/* Overlay and Loading Spinner */}
            {submitLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
        </>
    )
}
